import type {
  CustomerAddress,
  CustomerContact,
  CustomerDetail,
  CustomerFinancial,
  CustomerPreferences,
} from './types';

export const EMPTY_FINANCIAL: CustomerFinancial = {
  currency: 'USD',
  hold_status: false,
  credit_limit: null,
  receivable_account: '',
  tax_id: '',
  taxable: false,
  tax_code_id: null,
  payment_term_id: null,
};

export const EMPTY_PREFERENCES: CustomerPreferences = {
  preferred_communication: false,
  preferred_transactions: [],
};

export const EMPTY_CUSTOMER_DETAIL: CustomerDetail = {
  id: 0,
  customer_id: '',
  name: '',
  display_name: null,
  email: null,
  phone: null,
  category_id: null,
  category_name: null,
  active: true,
  linked_deposits_count: 0,
  fax: '',
  website: '',
  is_1099_enabled: false,
  ein: '',
  financial: { ...EMPTY_FINANCIAL },
  preferences: { ...EMPTY_PREFERENCES, preferred_transactions: [] },
  contacts: [],
  addresses: [],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function validateCustomerDetail(values: CustomerDetail): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = values.name.trim();
  if (name.length < 1 || name.length > 255) errors.name = 'validation.name';
  const displayName = (values.display_name || '').trim();
  if (displayName.length > 255) errors.display_name = 'validation.displayNameMax';
  const email = (values.email || '').trim();
  if (email && (email.length < 5 || email.length > 255 || !EMAIL_RE.test(email))) errors.email = 'validation.email';
  for (const field of ['phone', 'fax'] as const) {
    const raw = (values[field] || '').trim();
    if (!raw) continue;
    const digits = digitsOnly(raw);
    if (digits.length < 10 || digits.length > 15) errors[field] = 'validation.phone';
  }
  if (values.website.trim().length > 255) errors.website = 'validation.websiteMax';
  const taxId = values.financial.tax_id.trim();
  if (taxId && (taxId.length < 5 || taxId.length > 50)) errors['financial.tax_id'] = 'validation.taxId';
  if (values.financial.hold_status) {
    const limit = values.financial.credit_limit;
    if (!limit || Number(limit) <= 0) errors['financial.credit_limit'] = 'validation.creditLimit';
  }
  if (values.financial.taxable && values.financial.tax_code_id === null) {
    errors['financial.tax_code_id'] = 'validation.taxCodeRequired';
  }
  if (values.preferences.preferred_communication && !email) {
    errors.preferred_communication = 'validation.prefEmailRequired';
  }
  values.contacts.forEach((contact, index) => {
    const prefix = `contacts.${index}`;
    if (!contact.first_name.trim()) errors[`${prefix}.first_name`] = 'validation.contactFirst';
    if (!contact.last_name.trim()) errors[`${prefix}.last_name`] = 'validation.contactLast';
    const cEmail = contact.email.trim();
    if (!cEmail || cEmail.length < 5 || !EMAIL_RE.test(cEmail)) errors[`${prefix}.email`] = 'validation.contactEmail';
    const cPhone = contact.phone.trim();
    if (cPhone) {
      const digits = digitsOnly(cPhone);
      if (digits.length < 10 || digits.length > 15) errors[`${prefix}.phone`] = 'validation.phone';
    }
  });
  return errors;
}

export function newEmptyContact(): CustomerContact {
  return { salutation: '', first_name: '', middle_name: '', last_name: '', title: '', email: '', phone: '' };
}

export function newEmptyAddress(): CustomerAddress {
  return {
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    default_billing: false,
    default_shipping: false,
  };
}

export function mapServerFieldErrors(body: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof body !== 'object' || body === null) return out;
  const detail: unknown = 'detail' in body ? body.detail : null;
  if (Array.isArray(detail)) {
    for (const item of detail) {
      if (typeof item !== 'object' || item === null) continue;
      const field: unknown = 'field' in item ? item.field : null;
      const message: unknown = 'message' in item ? item.message : null;
      if (typeof field === 'string' && typeof message === 'string') out[field] = message;
    }
    return out;
  }
  if (typeof detail === 'string' && detail.trim()) out._form = detail;
  return out;
}
